import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Action, State, StateContext } from "@ngxs/store";
import { createRequestAction, RequestState } from "ngxs-requests-plugin";
import { environment } from "../../../../environments/environment";
import { LocalStorageService } from "../../services/local-storage.service";

import { GetOwnershipMetadata, GetOwnershipMetadataSuccess, GetOwnershipMetadataFail, GetOwnershipNodesByHash, GetOwnershipNodesByHashSuccess, GetOwnershipNodesByHashFail, GetOwnershipEdgesByHash, GetOwnershipEdgesByHashSuccess, GetOwnershipEdgesByHashFail } from "./ownership.actions";
import { IOwnershipMetadata, IOwnershipMetadataContent } from "../../interfaces/ownership.interface";

export interface OwnershipStateModel {
    ownershipMetadata: { [firmId: string] : 
        { [year: number]: IOwnershipMetadataContent }};
    ownershipNodes: { [firmId: string]:
        { [year: number]: string}};
    ownershipEdges: { [firmId: string]: 
        { [year: number]: string}};
    hashes: {[firmId: string]: string};
    refreshingHashes: Set<string>; // Track hash-year combinations being refreshed
}

/**
 * Immutably updates a single property in a hash-based object
 * @param obj - The original object to update
 * @param hash - The key to update
 * @param value - The new value to set
 * @returns A new object with the updated value
 */
export function patchObjectByFirmId<T>(
  obj: { [hash: string]: T }, 
  firmId: string, 
  value: T
): { [firmId: string]: T } {
  return {
    ...obj,
    [firmId]: value
  };
}

@RequestState('getOwnershipMetadata')
@Injectable()
export class GetOwnershipMetadataRequestState {}

@RequestState('getOwnershipNodesByHash')
@Injectable()
export class GetOwnershipNodesByHashRequestState {}

@RequestState('getOwnershipEdgesByHash')
@Injectable()
export class GetOwnershipEdgesByHashRequestState {}


@State<OwnershipStateModel>({
    name: 'ownershipState',
    defaults: {
        ownershipMetadata: {},
        ownershipNodes: {},
        ownershipEdges: {},
        hashes: {},
        refreshingHashes: new Set()
    }
})
@Injectable()
export class OwnershipState {
    constructor(
        private http: HttpClient,
        private localStorage: LocalStorageService
    ) {}

    // Metadata
    // Metadata| Request
    @Action(GetOwnershipMetadata)
    getOwnershipMetadata(
        {dispatch}: StateContext<OwnershipStateModel>,
        { payload: {firmId, year} }: GetOwnershipMetadata
    ) {
        let params = new HttpParams().set('firmId', firmId);
        if (year) {
            params = params.set('year', year.toString());
        }
        const request = this.http.get(environment.api + 'db/visualisation/ownership', {params});

        return dispatch([
            createRequestAction({
                request,
                state: GetOwnershipMetadataRequestState,
                successAction: GetOwnershipMetadataSuccess,
                failAction: GetOwnershipMetadataFail,
                metadata: {firmId, year}
            })
        ]);
    }

    // Metadata| Success
    @Action(GetOwnershipMetadataSuccess)
    getOwnershipMetadataSuccess(
        { dispatch, patchState, getState }: StateContext<OwnershipStateModel>,
        { payload: response, metadata: { firmId, year } }: GetOwnershipMetadataSuccess
    ) {
        if (response.status === 'empty') {
            const currentState = getState();
            const emptyMetadata: IOwnershipMetadataContent = {
                all_years: [],
                fiscal_year: null,
                ovHash: ''
            };
            const currentFirmMetadata = currentState.ownershipMetadata[firmId] || {};
            const updatedFirmMetadata = { ...currentFirmMetadata, [year == null ? 9999 : year]: emptyMetadata };
            const updatedMetadata = patchObjectByFirmId(currentState.ownershipMetadata, firmId, updatedFirmMetadata);
            patchState({ownershipMetadata: updatedMetadata});
        } else if (response.status === 'available') {
            const hash = response.hash
            const metadata = response.content
            metadata['ovHash'] = hash;  // Store hash in metadata for reference

            // Update state
            const currentState = getState();
            const currentFirmMetadata = currentState.ownershipMetadata[firmId] || {};
            const updatedFirmMetadata = { ...currentFirmMetadata, [year == null ? 9999 : year]: metadata };
            const updatedMetadata = patchObjectByFirmId(currentState.ownershipMetadata, firmId, updatedFirmMetadata);
            const updatedHash = patchObjectByFirmId(currentState.hashes, firmId, hash);
            
            // Clear any refresh tracking for this hash-year combination
            const targetYear = year || metadata.fiscal_year;
            const refreshKey = `${hash}-${targetYear}`;
            const updatedRefreshingHashes = new Set(currentState.refreshingHashes);
            updatedRefreshingHashes.delete(refreshKey);
            
            patchState({
                ownershipMetadata: updatedMetadata,
                hashes: updatedHash,
                refreshingHashes: updatedRefreshingHashes
            });

            // Get nodes and edges for the target year
            dispatch(new GetOwnershipNodesByHash({hash, year: targetYear}));
            dispatch(new GetOwnershipEdgesByHash({hash, year: targetYear}));
        }
    }

    // Metadata| Fail
    @Action(GetOwnershipMetadataFail)
    getOwnershipMetadataFail(
        { patchState, getState }: StateContext<OwnershipStateModel>,
        { payload: error }: GetOwnershipMetadataFail
    ) {
        console.error('Failed to get ownership metadata', error);
    }

    // ~~~~~ //
    // Nodes
    // Nodes| Request
    @Action(GetOwnershipNodesByHash)
    getOwnershipNodesByHash(
        {dispatch}: StateContext<OwnershipStateModel>,
        { payload: {hash, year} }: GetOwnershipNodesByHash
    ) {
        const params = new HttpParams().set('hash', hash).set('part', 'nodes').set('year', year.toString());
        const headers = new HttpHeaders({'Accept-Encoding': 'gzip'});
        const request = this.http.get(
            environment.api + 'db/visualisation/ownership/parts', {
                params,
                headers,
                responseType: 'text'
        });

        return dispatch(
            createRequestAction({
                request,
                state: GetOwnershipNodesByHashRequestState,
                successAction: GetOwnershipNodesByHashSuccess,
                failAction: GetOwnershipNodesByHashFail,
                metadata: {hash, year}
            })
        );
    }

    // Nodes| Success
    @Action(GetOwnershipNodesByHashSuccess)
    getOwnershipNodesByHashSuccess(
        { dispatch, patchState, getState }: StateContext<OwnershipStateModel>,
        { payload: response, metadata: {hash, year} }: GetOwnershipNodesByHashSuccess
    ) {
        const nodeData = response;

        // Update state
        const currentState = getState();
        const firmId = Object.keys(currentState.hashes).find(key => currentState.hashes[key] === hash);
        if (!firmId) {console.error('Firm ID not found for hash:', hash); return;}

        const currentFirmNodes = currentState.ownershipNodes[firmId] || {};
        const updatedFirmNodes = { ...currentFirmNodes, [year]: nodeData };
        const updatedNodes = patchObjectByFirmId(currentState.ownershipNodes, firmId, updatedFirmNodes);
        patchState({ownershipNodes: updatedNodes});
    }

    // Nodes| Fail
    @Action(GetOwnershipNodesByHashFail)
    getOwnershipNodesByHashFail(
        { dispatch, getState, patchState }: StateContext<OwnershipStateModel>,
        { payload: error, metadata: { hash, year } }: GetOwnershipNodesByHashFail
    ) {       
        // Check if it's a 404 hash_expired error
        if (error.status === 404 && error.error && JSON.parse(error.error).error === "hash_expired") {
            console.log('Hash expired for nodes, checking if refresh needed for year:', year);
            
            const currentState = getState();
            const refreshKey = `${hash}-${year}`;
            
            // Check if we're already refreshing this hash-year combination
            if (currentState.refreshingHashes.has(refreshKey)) {
                console.log('Metadata refresh already in progress for hash-year:', refreshKey);
                return;
            }
            
            // Find firmId by hash
            const firmId = Object.keys(currentState.hashes).find(key => currentState.hashes[key] === hash);
            
            if (firmId) {
                // Mark this hash-year as being refreshed
                const updatedRefreshingHashes = new Set(currentState.refreshingHashes);
                updatedRefreshingHashes.add(refreshKey);
                patchState({ refreshingHashes: updatedRefreshingHashes });
                
                console.log('Starting metadata refresh for hash-year:', refreshKey);
                // Re-dispatch metadata request with the year
                dispatch(new GetOwnershipMetadata({ firmId, year }));
            } else {
                console.error('Firm ID not found for expired hash:', hash);
            }
        } else {
            console.error('Failed to get ownership nodes', error);
        }
    }

    // ~~~~~ //
    // Edges
    // Edges| Request
    @Action(GetOwnershipEdgesByHash)
    getOwnershipEdgesByHash(
        {dispatch}: StateContext<OwnershipStateModel>,
        { payload: {hash, year} }: GetOwnershipEdgesByHash
    ) {
        const params = new HttpParams().set('hash', hash).set('part', 'edges').set('year', year.toString());
        const headers = new HttpHeaders({'Accept-Encoding': 'gzip'});
        const request = this.http.get(
            environment.api + 'db/visualisation/ownership/parts', {
                params,
                headers,
                responseType: 'text'
        });

        return dispatch(
            createRequestAction({
                request,
                state: GetOwnershipEdgesByHashRequestState,
                successAction: GetOwnershipEdgesByHashSuccess,
                failAction: GetOwnershipEdgesByHashFail,
                metadata: {hash, year}
            })
        );
    }

    // Edges| Success
    @Action(GetOwnershipEdgesByHashSuccess)
    getOwnershipEdgesByHashSuccess(
        { patchState, getState }: StateContext<OwnershipStateModel>,
        { payload: response, metadata: {hash, year} }: GetOwnershipEdgesByHashSuccess
    ) {
        const edgeData = response;

        // Update state
        const currentState = getState();
        const firmId = Object.keys(currentState.hashes).find(key => currentState.hashes[key] === hash);
        if (!firmId) {console.error('Firm ID not found for hash:', hash); return;}

        const currentFirmEdges = currentState.ownershipEdges[firmId] || {};
        const updatedFirmEdges = { ...currentFirmEdges, [year]: edgeData };
        const updatedEdges = patchObjectByFirmId(currentState.ownershipEdges, firmId, updatedFirmEdges);
        patchState({ownershipEdges: updatedEdges});
    }

    // Edges| Fail
    @Action(GetOwnershipEdgesByHashFail)
    getOwnershipEdgesByHashFail(
        { dispatch, getState, patchState }: StateContext<OwnershipStateModel>,
        { payload: error, metadata: { hash, year } }: GetOwnershipEdgesByHashFail
    ) {       
        // Check if it's a 404 hash_expired error
        if (error.status === 404 && error.error && JSON.parse(error.error).error === "hash_expired") {
            console.log('Hash expired for edges, checking if refresh needed for year:', year);
            
            const currentState = getState();
            const refreshKey = `${hash}-${year}`;
            
            // Check if we're already refreshing this hash-year combination
            if (currentState.refreshingHashes.has(refreshKey)) {
                console.log('Metadata refresh already in progress for hash-year:', refreshKey);
                return;
            }
            
            // Find firmId by hash
            const firmId = Object.keys(currentState.hashes).find(key => currentState.hashes[key] === hash);
            
            if (firmId) {
                // Mark this hash-year as being refreshed
                const updatedRefreshingHashes = new Set(currentState.refreshingHashes);
                updatedRefreshingHashes.add(refreshKey);
                patchState({ refreshingHashes: updatedRefreshingHashes });
                
                console.log('Starting metadata refresh for hash-year:', refreshKey);
                // Re-dispatch metadata request with the year
                dispatch(new GetOwnershipMetadata({ firmId, year }));
            } else {
                console.error('Firm ID not found for expired hash:', hash);
            }
        } else {
            console.error('Failed to get ownership edges', error);
        }
    }            
}